import {
  NodonEnvironment,
  NodonHost,
  ServerEnvironment,
} from '../models/environment.model';
import { isOneOf } from './array.helpers';

/** Hostnames that always resolve to the machine the process runs on. */
const LOCAL_HOSTNAMES: ReadonlySet<string> = new Set([
  NodonHost.Localhost,
  '127.0.0.1',
  '0.0.0.0',
  '::1',
  '[::1]',
]);

export const getProcessEnvironment = (): NodeJS.ProcessEnv | undefined => {
  if (typeof process === 'object') {
    return process.env;
  }
  return undefined;
};

/**
 * True when the hostname points at the local machine. Used by safeguards that
 * must never be active outside a developer machine, e.g. disabled auth.
 */
export const isLocalHostname = (hostname: string): boolean =>
  LOCAL_HOSTNAMES.has(hostname.toLowerCase());

/**
 * True for Cloudflare quick-tunnel hosts (`npm run browserless:tunnel`).
 * These are public HTTPS, not loopback — do not treat them as
 * {@link isLocalHostname}.
 */
export const isCloudflareQuickTunnelHostname = (hostname: string): boolean =>
  hostname.toLowerCase().endsWith('.trycloudflare.com');

/**
 * Environment for a browser hostname.
 * Loopback addresses (`127.0.0.1`, `::1`) and Cloudflare quick tunnels map
 * to localhost — they are the same app as {@link NodonHost.Localhost}.
 */
export const getHostnameEnvironment = (
  hostname: string,
): NodonEnvironment | undefined => {
  if (isLocalHostname(hostname) || isCloudflareQuickTunnelHostname(hostname)) {
    return NodonEnvironment.Localhost;
  }
  if (hostname === NodonHost.Development) return NodonEnvironment.Development;
  if (hostname === NodonHost.Production) return NodonEnvironment.Production;
  return undefined;
};

/**
 * Hostname of a URL, or undefined when there is none to read.
 * @param url Absolute URL, e.g. `postgres://user:pass@localhost:5432/nodon`
 */
export const getUrlHostname = (url: string): string | undefined => {
  try {
    return new URL(url).hostname;
  } catch {
    // `new URL` rejects unencoded `#` and `?` in a password, which is common
    // enough in connection strings that a local host must not be lost to it
    return getAuthorityHostname(url);
  }
};

/** `scheme://user:pass@host:port/database` → `host`. */
const getAuthorityHostname = (url: string): string | undefined => {
  const authority = url.split('://')[1]?.split('/')[0];
  if (!authority) return undefined;

  // The host is whatever follows the last `@`, so a password may contain one
  const hostAndPort = authority.slice(authority.lastIndexOf('@') + 1);
  const host = hostAndPort.startsWith('[')
    ? hostAndPort.slice(0, hostAndPort.indexOf(']') + 1) // IPv6 literal
    : hostAndPort.split(':')[0];

  return host || undefined;
};

/**
 * True when a connection string points at a database on the local machine.
 * A connection string with no readable host counts as non-local, so callers
 * fail closed.
 * @param url Connection string, e.g. `postgres://user:pass@localhost:5432/nodon`
 */
export const isLocalDatabaseUrl = (url: string): boolean => {
  const hostname = getUrlHostname(url);
  if (!hostname) return false;
  return isLocalHostname(hostname);
};

/**
 * Get the server environment from the process environment.
 * In client use getConfig() instead.
 * @returns The server environment.
 */
export const getServerEnvironment = (): ServerEnvironment => {
  const env = getProcessEnvironment() as ServerEnvironment;

  const guide =
    '. Please set the environment variables in the .env file or in the environment varaibles in google cloud.';

  const envs = Object.values(NodonEnvironment);

  if (!isOneOf(envs, env.ENVIRONMENT)) {
    throw new Error(
      'ENVIRONMENT is not set. Environment must be one of: ' +
        envs.join(', ') +
        guide,
    );
  }

  // Either DATABASE_URL or all DB_* must be set
  if (
    !env.DATABASE_URL &&
    ![
      env.DB_HOST,
      env.DB_PORT,
      env.DB_USER,
      env.DB_PASSWORD,
      env.DB_DATABASE,
    ].every(Boolean)
  ) {
    throw new Error(
      'Either DATABASE_URL or all of DB_HOST, DB_PORT, DB_USER, DB_PASSWORD, DB_DATABASE must be set' +
        guide,
    );
  }

  return env;
};

/**
 * Get the client environment from the window location.
 * NOTE:
 * 1. ONLY use this in the browser client.
 * 2. unmapped hostnames will throw an error. Known hosts: loopback,
 *    Cloudflare quick tunnels, {@link NodonHost}.
 * @returns The client environment.
 */
export const getURLEnvironment = (window: Window): NodonEnvironment => {
  const hostname = window.location.hostname;
  const env = getHostnameEnvironment(hostname);
  if (!env) {
    console.error('Unknown environment: ', hostname);
    throw new Error(`Unknown environment: ${hostname}.`);
  }
  return env;
};

/**
 * Get the environment from the window location or the server environment.
 * Unknown browser hosts throw — same as {@link getURLEnvironment}.
 */
export const getEnvironment = (): NodonEnvironment => {
  if (typeof window !== 'undefined') {
    return getURLEnvironment(window);
  }
  const environment = getServerEnvironment().ENVIRONMENT;
  switch (environment) {
    case NodonEnvironment.Localhost:
      return NodonEnvironment.Localhost;
    case NodonEnvironment.Development:
      return NodonEnvironment.Development;
    case NodonEnvironment.Production:
      return NodonEnvironment.Production;
    default: {
      throw new Error(`Unknown environment: ${environment}.`);
    }
  }
};

/**
 * Check if the current environment is the given environment.
 * @param env The environment to check.
 * @returns True if the current environment is the given environment, false otherwise.
 */
export const isEnvironment = (
  env: ServerEnvironment['ENVIRONMENT'],
): boolean => {
  return getEnvironment() === env;
};

/**
 * True for local and test server environments (not production).
 * Use for dev-only tooling such as manual PDF generation_key debugging.
 */
export const isNonProductionEnvironment = (
  environment: ServerEnvironment['ENVIRONMENT'],
): boolean =>
  environment === NodonEnvironment.Localhost ||
  environment === NodonEnvironment.Development;
